import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/** Minimum pointer movement (px) before a gesture counts as a drag instead of a click. */
const DRAG_THRESHOLD_PX = 6;

/** Momentum friction (px/ms²) — higher = stops sooner. */
const MOMENTUM_FRICTION = 0.0035;

/** Stop momentum when speed falls below this (px/ms). */
const MOMENTUM_MIN_VELOCITY = 0.02;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    // DAC customization: opt-in drag — only when host sets data-draggable
    if (this.#isDraggable) {
      this.addEventListener('pointerdown', this.#handlePointerDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#cancelMomentum();
    this.#dragController?.abort();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /** DAC customization: true while pointer is captured for a drag gesture */
  #dragging = false;

  /** DAC customization: CSS animation cancelled; position driven by inline transform */
  #manualMode = false;

  /**
   * DAC customization: current translateX while in manual / drag mode
   * @type {number}
   */
  #manualOffset = 0;

  /**
   * DAC customization: abort controller for active drag listeners
   * @type {AbortController | null}
   */
  #dragController = null;

  /**
   * DAC customization: rAF id for momentum coast
   * @type {number | null}
   */
  #momentumRaf = null;

  /**
   * DAC customization: cached loop width while dragging (avoids layout thrash per move)
   * @type {number}
   */
  #loopDistance = 0;

  /** DAC customization: drag enabled only when data-draggable is present */
  get #isDraggable() {
    return this.hasAttribute('data-draggable');
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;
    // DAC customization: skip hover slowdown while dragging or in manual momentum
    if (this.#dragging || this.#manualMode) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    // DAC customization: do not resume hover speed while drag/manual owns the track
    if (this.#dragging || this.#manualMode) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  /**
   * DAC customization: start drag / pause-and-scrub gesture when data-draggable is set.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!this.#isDraggable) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    if (!(event.target instanceof Element)) return;
    // Ignore interactive controls inside marquee content other than the track itself
    if (event.target.closest('button, input, select, textarea')) return;

    this.#cancelMomentum();
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;

    const startX = event.clientX;
    const startY = event.clientY;
    let lastX = startX;
    let lastTime = performance.now();
    let velocity = 0;
    let moved = false;
    let dragStarted = false;

    this.#dragController?.abort();
    const controller = new AbortController();
    this.#dragController = controller;
    const { signal } = controller;

    /**
     * @param {PointerEvent} moveEvent
     */
    const onPointerMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (!dragStarted) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        // Prefer vertical page scroll when the gesture is clearly vertical
        if (Math.abs(dy) > Math.abs(dx)) {
          controller.abort();
          return;
        }

        dragStarted = true;
        moved = true;
        this.#dragging = true;
        this.setAttribute('dragging', '');
        this.#enterManualMode();
        this.setPointerCapture(moveEvent.pointerId);
      }

      moveEvent.preventDefault();

      const now = performance.now();
      const frameDx = moveEvent.clientX - lastX;
      const dt = Math.max(now - lastTime, 1);
      velocity = frameDx / dt;
      lastX = moveEvent.clientX;
      lastTime = now;

      this.#setManualOffset(this.#manualOffset + frameDx);
    };

    /**
     * @param {PointerEvent} upEvent
     */
    const onPointerUp = (upEvent) => {
      const didDrag = dragStarted;
      controller.abort();

      if (!didDrag) {
        this.#dragging = false;
        return;
      }

      this.#dragging = false;
      this.removeAttribute('dragging');

      try {
        this.releasePointerCapture(upEvent.pointerId);
      } catch {
        // Pointer may already be released
      }

      // Register AFTER abort — click fires after pointerup, so it must not share
      // the aborted signal or the suppressor is removed before it can run.
      document.addEventListener('click', preventDefault, { capture: true, once: true });
      // Touch / cancelled paths may never emit click; drop the listener either way.
      setTimeout(() => {
        document.removeEventListener('click', preventDefault, { capture: true });
      }, 500);

      if (moved && Math.abs(velocity) >= MOMENTUM_MIN_VELOCITY) {
        this.#runMomentum(velocity);
      } else {
        this.#resumeFromManual();
      }
    };

    document.addEventListener('pointermove', onPointerMove, { signal, passive: false });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /**
   * DAC customization: freeze the live translate, then disable CSS animation so it
   * cannot restart and override the inline transform (that caused drag flicker).
   */
  #enterManualMode() {
    const { wrapper } = this.refs;
    this.#loopDistance = this.#measureLoopDistance();
    const x = this.#normalizeOffset(this.#getCurrentTranslateX());
    this.#manualOffset = x;
    this.#manualMode = true;

    // Hold the current frame first — then kill animation in the same turn
    wrapper.style.transform = `translate3d(${x}px, 0, 0)`;
    wrapper.style.animation = 'none';

    for (const animation of wrapper.getAnimations()) {
      animation.cancel();
    }
  }

  /**
   * DAC customization: apply wrapped translate while in manual mode.
   * @param {number} x
   */
  #setManualOffset(x) {
    this.#manualOffset = this.#normalizeOffset(x);
    this.refs.wrapper.style.transform = `translate3d(${this.#manualOffset}px, 0, 0)`;
  }

  /**
   * DAC customization: keep offset in (-loop, 0] so the duplicated track stays seamless.
   * @param {number} x
   * @returns {number}
   */
  #normalizeOffset(x) {
    const loop = this.#loopDistance || this.#measureLoopDistance();
    if (loop <= 0) return x;

    let normalized = x % loop;
    if (normalized > 0) normalized -= loop;
    if (normalized <= -loop) normalized += loop;
    return normalized;
  }

  /**
   * DAC customization: one content width + flex gap — matches CSS -50% - gap/2 loop.
   * @returns {number}
   */
  #measureLoopDistance() {
    const { content, wrapper } = this.refs;
    const gap = Number.parseFloat(getComputedStyle(wrapper).columnGap || getComputedStyle(wrapper).gap) || 0;
    return content.offsetWidth + gap;
  }

  /**
   * DAC customization: read live translateX from computed style (animation or inline).
   * @returns {number}
   */
  #getCurrentTranslateX() {
    const { wrapper } = this.refs;
    const transform = getComputedStyle(wrapper).transform;
    if (!transform || transform === 'none') return this.#manualMode ? this.#manualOffset : 0;

    try {
      return new DOMMatrixReadOnly(transform).m41;
    } catch {
      return this.#manualOffset;
    }
  }

  /**
   * DAC customization: coast after release, then rejoin the CSS marquee animation.
   * @param {number} velocityPxPerMs
   */
  #runMomentum(velocityPxPerMs) {
    this.#cancelMomentum();

    let velocity = velocityPxPerMs;
    let lastTime = performance.now();

    /**
     * @param {number} now
     */
    const tick = (now) => {
      if (!this.#manualMode || this.#dragging) {
        this.#momentumRaf = null;
        return;
      }

      const dt = now - lastTime;
      lastTime = now;

      this.#setManualOffset(this.#manualOffset + velocity * dt);

      const sign = Math.sign(velocity);
      velocity -= sign * MOMENTUM_FRICTION * dt;

      if (Math.sign(velocity) !== sign || Math.abs(velocity) < MOMENTUM_MIN_VELOCITY) {
        this.#momentumRaf = null;
        this.#resumeFromManual();
        return;
      }

      this.#momentumRaf = requestAnimationFrame(tick);
    };

    this.#momentumRaf = requestAnimationFrame(tick);
  }

  /** DAC customization: stop momentum rAF */
  #cancelMomentum() {
    if (this.#momentumRaf !== null) {
      cancelAnimationFrame(this.#momentumRaf);
      this.#momentumRaf = null;
    }
  }

  /**
   * DAC customization: rejoin CSS animation at the scrubbed offset without a visible jump.
   */
  #resumeFromManual() {
    if (!this.#manualMode) return;

    const { wrapper } = this.refs;
    const prefersMotion = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    const canAnimate = prefersMotion && !this.hasAttribute('data-disabled');

    // No CSS marquee to rejoin — keep the scrubbed translate where the user left it
    if (!canAnimate) {
      this.#manualMode = false;
      return;
    }

    const loop = this.#loopDistance || this.#measureLoopDistance();
    const progress = loop > 0 ? Math.min(Math.abs(this.#manualOffset) / loop, 1) : 0;
    const direction = this.getAttribute('data-movement-direction') || 'normal';

    // Keep inline transform visible while we rebuild the stylesheet animation underneath
    wrapper.style.animation = 'none';
    void wrapper.offsetWidth;
    wrapper.style.animation = '';

    const animation = wrapper.getAnimations()[0];
    if (animation?.effect) {
      const duration = Number(animation.effect.getComputedTiming().duration) || 0;
      if (duration) {
        animation.pause();
        // Reverse animation-direction maps offset → currentTime inverted
        const animationProgress = direction === 'reverse' ? 1 - progress : progress;
        animation.currentTime = animationProgress * duration;
        animation.playbackRate = 1;
        // Drop inline override only after the paused animation is on the same frame
        wrapper.style.transform = '';
        animation.play();
      } else {
        wrapper.style.transform = '';
      }
    } else {
      wrapper.style.transform = '';
    }

    this.#manualMode = false;

    if (this.matches(':hover')) {
      this.#slowDown();
    }
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    // DAC customization: drop manual/drag state before rebuild so animation can restart cleanly
    this.#cancelMomentum();
    this.#dragController?.abort();
    this.#dragging = false;
    this.#manualMode = false;
    this.removeAttribute('dragging');
    this.refs.wrapper.style.transform = '';
    this.refs.wrapper.style.animation = '';

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * @param {Event} event
 */
function preventDefault(event) {
  event.preventDefault();
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
